import type * as z from 'zod';
import {
  productBannerResponseSchema,
  productBannerSchema,
  restaurantBannerResponseSchema,
  restaurantBannerSchema,
  type ListResult,
  type PaginationParams,
  type ProductBanner,
  type RestaurantBanner,
} from '../model';
import type { ApiLocale } from '@noor/api';
import {
  deleteOne,
  getList,
  getOne,
  patchOne,
  postOne,
  type RequestOptions,
} from './http';

// Restaurant Banner Service + Product Banner Service — the promo-carousel
// banners. The two services are identical apart from their path, their item
// schema and the product banner's extra `product_id`, so one factory builds
// both clients rather than duplicating six methods.

/** Filters shared by both banner list endpoints. */
export type ListBannersParams = PaginationParams & {
  restaurant_id?: string;
};

/** Fields common to both create bodies. */
interface BannerFieldsInput {
  restaurant_id: string;
  title?: string;
  image?: string;
  action_url?: string;
  position?: number;
}

/** `POST /restaurant_banners` body. */
export type CreateRestaurantBannerInput = BannerFieldsInput;

/** `POST /product_banners` body. */
export interface CreateProductBannerInput extends BannerFieldsInput {
  product_id: string;
}

/**
 * `PATCH /{restaurant,product}-banners/{id}` body.
 *
 * NO `image`: banner photos became PER-LOCALE and slot-managed (2026-08-07),
 * and as of the 2026-08-14 revision they are written through the Image Service
 * — {@link imageApi.upsert} / {@link imageApi.remove}, entity type
 * `restaurant_banner` or `product_banner`, selecting by `locale`. The gateway
 * rejects unknown fields, so keeping `image` here would fail the whole update.
 */
export interface UpdateBannerInput {
  title?: string;
  action_url?: string;
  is_active?: boolean;
  position?: number;
}

/**
 * The locale a banner photo belongs to. Unlike a restaurant's image type, this
 * partitions the SAME picture slot by language — a banner is artwork with text
 * baked in, so each language needs its own file. Writing one locale leaves the
 * other two untouched.
 *
 * The artwork itself is written through the Image Service (`imageApi`) as of
 * the 2026-08-14 spec revision: this type now names the `locale` on an upsert
 * item and the `{selector}` on a delete. The old
 * `POST/DELETE {…}-banners/{id}/image[/{locale}]` routes are gone.
 */
export type BannerImageLocale = ApiLocale;

/** The CRUD surface both banner services expose. */
interface BannerApi<Banner, CreateInput> {
  list(params?: ListBannersParams, options?: RequestOptions): Promise<ListResult<Banner>>;
  get(id: string, options?: RequestOptions): Promise<Banner>;
  create(input: CreateInput): Promise<Banner>;
  update(id: string, input: UpdateBannerInput): Promise<Banner>;
  remove(id: string): Promise<void>;
}

/**
 * Build a banner client for one of the two services. `responseSchema` unwraps
 * the single-resource `{ banner }` payload both services return.
 */
function createBannerApi<Item extends z.ZodType, CreateInput>(
  path: string,
  item: Item,
  responseSchema: z.ZodType<{ banner: z.infer<Item> }>,
): BannerApi<z.infer<Item>, CreateInput> {
  return {
    list(params, options) {
      return getList(path, item, params, options);
    },
    async get(id, options) {
      const { banner } = await getOne(
        `${path}/${id}`,
        responseSchema,
        undefined,
        options,
      );
      return banner;
    },
    async create(input) {
      const { banner } = await postOne(path, input, responseSchema);
      return banner;
    },
    async update(id, input) {
      const { banner } = await patchOne(`${path}/${id}`, input, responseSchema);
      return banner;
    },
    remove(id) {
      return deleteOne(`${path}/${id}`);
    },
  };
}

// Paths are HYPHENATED. They were `restaurant_banners` / `product_banners`
// until 2026-08-07; the underscore forms now 404 ("no Route matched"), so this
// is a rename, not an alias.
export const restaurantBannerApi = createBannerApi<
  typeof restaurantBannerSchema,
  CreateRestaurantBannerInput
>('restaurant-banners', restaurantBannerSchema, restaurantBannerResponseSchema);

export const productBannerApi = createBannerApi<
  typeof productBannerSchema,
  CreateProductBannerInput
>('product-banners', productBannerSchema, productBannerResponseSchema);

export type { RestaurantBanner, ProductBanner };
